import { ApplicationPrioritizationService, ApplicationService, SchemaService } from '@amzn/awscat-portfolio-assessment-service-client';
import { AttrConfig, CriticalityMap, DeserializedApplication, SchemaProperty } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { SortingState } from '@amzn/awsui-collection-hooks/dist/cjs/interfaces';
import { Alert, Box, ButtonDropdown, SpaceBetween } from '@amzn/awsui-components-react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { useIsMounted } from 'react-tidy';

import { HelpPanelTopicOptions } from '../../../common/Constants';
import { constants } from '../../../common/Localization';
import AWSCATTable from '../../../common/table/Table-index';
import { ColumnDefinition } from '../../../common/table/types';
import { MatchParam } from '../../../common/types';
import { HelpPanelContext } from '../../../contexts/HelpPanelContext';
import { PortfolioContext } from '../../../contexts/PortfolioContext';
import { ColumnOrderType } from '../../portfolio-data-asset/constants';
import { SchemaPropertyMap } from '../../portfolio-data-asset/types';
import { generateColumnDefs } from '../../portfolio-data-asset/utils';
import ApplicationPrioritizationGraph from './ApplicationPrioritizationGraph';
import { buildAttributePartitionFromValues, getUniqueAttributeValues } from './ApplicationPrioritizationHelper';
import ApplicationPrioritizationWizard from './ApplicationPrioritizationWizard';
import { PRIORITIZATION_ACTIONS, STAGE_VALUES, VISIBLE_COLUMNS } from './constants';
type ApplicationPrioritizationProps = RouteComponentProps<MatchParam> & FlashContextInterface;
export type AttributeSelectionOption = Omit<AttrConfig, 'id' | 'updatedAt'> & {
    label: string;
    invalidScoreMessage: string;
    invalidBoundMessage: string;
    value: string;
};
const applicationService = new ApplicationService();
const appPrioritizationService = new ApplicationPrioritizationService();
const schemaService = new SchemaService();

/**
 * Computes the difference between the last app was updated and the attribute config was updated, in milliseconds.
 * @param apps All applications whose update time to consider
 * @param attrConfigLastModified The last modified timestamp of the attribute config
 * @returns the difference in the update time of the attribute config and the apps in milliseconds
 */
export function computeTimeDiff(
    apps: DeserializedApplication[],
    attrConfigLastModified: Date | null
): number {

    // Find out when the apps were most recently updated
    const appsLastModified = apps.length > 0 ?
        new Date(apps.map(app => app.updatedAt).reduce((date1, date2) => date1 > date2 ? date1 : date2)) :
        null;

    // Difference between when the apps and attibute config were last updated
    return !!attrConfigLastModified && !!appsLastModified ? +appsLastModified - +attrConfigLastModified : 0;
}

function ApplicationPrioritization({match, flash}: ApplicationPrioritizationProps) {
    const { alert } = flash;
    const portfolioId = match.params.portfolioId;

    const [ appPrioritizationState, setAppPrioritizationState ] = useState<STAGE_VALUES>(STAGE_VALUES.DEFAULT);
    const [ loading, setLoading ] = useState(true);
    const [ applicationItems, setApplicationItems ] = useState<DeserializedApplication[]>([]);
    const [ attrSelectOptions, setAttrSelectOptions ] = useState<AttributeSelectionOption[]>([]);
    const [ selectedAttributes, setSelectedAttributes ] = useState<AttributeSelectionOption[]>([]);
    const [ applicationColumnDefinitions, setApplicationColumnDefinitions ] = useState<ColumnDefinition<DeserializedApplication>[]>([]);
    const [ rankExists, setRankExists ] = useState(false);
    const [ activeAssessmentId, setActiveAssessmentId ] = useState('');
    const [ isAttrConfigStale, setIsAttrConfigStale ] = useState(false);
    const { portfolio } = useContext(PortfolioContext);
    const { panelHandler } = useContext(HelpPanelContext);

    useEffect(() => {
        panelHandler(HelpPanelTopicOptions.APPLICATION_PRIORITIZATION);
    }, [panelHandler]);
    
    const isMounted = useIsMounted();

    useEffect(() => {
        setActiveAssessmentId(portfolio.assessmentId);
    }, [ portfolio ]);

    const loadApplicationPrioritization = useCallback(async () => {
        setLoading(true);
        try {
            if (portfolioId) {
                // Load portfolio application
                const apps = await applicationService.list(portfolioId) as DeserializedApplication[];

                // Load application schemas
                let applicationSchemas: SchemaProperty[] = [];
                const applicationHeaders: SchemaPropertyMap = {};
                applicationSchemas = await schemaService.listAttributes('applications', portfolioId);
                applicationSchemas.forEach(appSchema => {
                    applicationHeaders[appSchema.value] = appSchema;
                });

                // Build application column definitions
                const columnDefinitions = generateColumnDefs(apps, applicationHeaders, ColumnOrderType.APPLICATION);

                // Load attributes config and generate attribute selection options
                const attrConfigs = await appPrioritizationService.getAttributeConfigs(portfolioId);

                // Load attribute config metadata
                const attrConfigLastModified = Object.keys(attrConfigs).length > 0 ? 
                    new Date(await appPrioritizationService.getAttributeConfigLastModified(portfolioId)) : 
                    null;

                const timeDiff = computeTimeDiff(apps, attrConfigLastModified);

                const selectedAttrs: AttributeSelectionOption[] = [];
                const attributeSelectionOptions: AttributeSelectionOption[] =
                    columnDefinitions.filter(colDef => applicationHeaders[colDef.id].prioritizable !== false).map(prioritizableSchema => {
                        let partition = attrConfigs[prioritizableSchema.id]?.partition;
                        if (!partition) {
                            const uniqueAttributeValues = getUniqueAttributeValues(prioritizableSchema, apps);
                            partition = buildAttributePartitionFromValues(prioritizableSchema, uniqueAttributeValues);
                        }

                        const selectionOption: AttributeSelectionOption = {
                            configCompleted: attrConfigs[prioritizableSchema.id]?.configCompleted || false,
                            value: prioritizableSchema.id,
                            invalidScoreMessage: '',
                            invalidBoundMessage: '',
                            label: prioritizableSchema.text || prioritizableSchema.id,
                            partition: partition,
                            selected: attrConfigs[prioritizableSchema.id]?.selected || false,
                        };

                        if (attrConfigs[prioritizableSchema.id]?.selected) {
                            selectedAttrs.push(selectionOption);
                        }
                        return selectionOption;
                    });

                // Filter out columns that are not displayed in app table
                const selectedAttrIds = selectedAttrs.map(att => att.value);
                const columnsInTable = columnDefinitions.filter(appColumnDef => VISIBLE_COLUMNS.concat(selectedAttrIds).includes(appColumnDef.id));

                if (isMounted()) {
                    // If there is a gap between when the configs and the apps were updated, show a warning
                    setIsAttrConfigStale(timeDiff > 30000);
                    setApplicationItems(apps);
                    setSelectedAttributes(selectedAttrs);
                    setAttrSelectOptions(attributeSelectionOptions);
                    setApplicationColumnDefinitions(columnsInTable);
                    // Determine if rank data exist
                    setRankExists(columnsInTable.some(columnDef => columnDef.id === 'ranking'));
                }
            }
        } catch (err) {
            if (isMounted()) {
                alert(FlashType.error, (err as Error).message, '');
                setApplicationItems([]);
                setApplicationColumnDefinitions([]);
            }
        } finally {
            if (isMounted()) {
                setLoading(false);
            }
        }
    },[alert, isMounted, portfolioId]);

    useEffect(() => {
        loadApplicationPrioritization();
    }, [ loadApplicationPrioritization ]);

    const editAppPrioritization = () => {
        setAppPrioritizationState(STAGE_VALUES.EDIT);
    };

    const cancelEditAppPrioritization = () => {
        setAppPrioritizationState(STAGE_VALUES.DEFAULT);
    };

    const saveAttrConfig = async (attributes: string): Promise<void> => {
        try {
            await appPrioritizationService.saveAttributeConfigs({
                attributes,
                portfolioId,
            });
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.APP_PRIORITIZATION_SAVE_CONFIGS_ERROR);
        }
    };

    const getCriticality = (): Promise<CriticalityMap> => {
        return appPrioritizationService.getCriticality(portfolioId);
    };

    const calculateRanking = async (criticality: string): Promise<void> => {
        setLoading(true);
        try {
            await appPrioritizationService.saveCriticality({
                criticalityMap: criticality,
                portfolioId,
            });

            await appPrioritizationService.rank(portfolioId, activeAssessmentId);
            if (isMounted()) {
                setAppPrioritizationState(STAGE_VALUES.DEFAULT);
            }
            await loadApplicationPrioritization();
        } catch (err) {
            if (isMounted()) {
                cancelEditAppPrioritization();
                flash.alert(FlashType.error, (err as Error).message, constants.APP_PRIORITIZATION_NETWORK_ERROR);
            }
        } finally {
            if (isMounted()) {
                setLoading(false);
            }
        }
    };

    const defaultSortingState: SortingState<DeserializedApplication> = {
        isDescending: false,
        sortingColumn: {
            sortingField: 'ranking',
            sortingComparator: (appA, appB) => {
                if (appA.ranking == null || appB.ranking == null) {
                    return appA.applicationId.localeCompare(appB.applicationId);
                }
                return appA.ranking - appB.ranking;
            },
        },
    };

    const ApplicationPrioritizationTable = (
        <AWSCATTable
            resourceName={constants.APP_PRIORITIZATION_TITLE}
            items={applicationItems}
            columnDefinitions={applicationColumnDefinitions}
            loading={loading}
            autoGenerateVisibleContent={true}
            defaultSortBy={defaultSortingState}
            useDefaultDownload
        />
    );

    const resetAppPrioritization = () => {
        setAppPrioritizationState(STAGE_VALUES.START_OVER);
    };

    const onActionClicked = (eventId: string) => {
        switch (eventId) {
            case STAGE_VALUES.START_OVER:
                resetAppPrioritization();
                break;
            case STAGE_VALUES.EDIT:
                editAppPrioritization();
                break;
        }
    };

    const attrConfigStaleBanner = isAttrConfigStale && (
        <Alert data-testid='stalePrioritizationWarning' header={constants.APP_CONFIG_STALE_HEADER}>
            <Box>{constants.APP_CONFIG_STALE_P1}</Box>
        </Alert>
    );

    if (appPrioritizationState === STAGE_VALUES.DEFAULT) {
        return (
            <SpaceBetween size="l">
                {attrConfigStaleBanner}
                {rankExists && (
                    <Box float="right">
                        <SpaceBetween
                            direction='horizontal'
                            size='xs'>
                            <ButtonDropdown
                                disabled={false}
                                onItemClick={ event => onActionClicked(event.detail.id)}
                                items={PRIORITIZATION_ACTIONS}>
                                {constants.ACTION_BUTTON_TEXT}
                            </ButtonDropdown>
                        </SpaceBetween>
                    </Box>
                )}
                <ApplicationPrioritizationGraph
                    loading={loading}
                    applications={applicationItems}
                    rankExist={rankExists}
                    getStarted={editAppPrioritization}
                />
                {ApplicationPrioritizationTable}
            </SpaceBetween>
        );
    } else if (appPrioritizationState === STAGE_VALUES.EDIT) {
        return (
            <ApplicationPrioritizationWizard
                attrSelectionOptions={attrSelectOptions}
                selectedAttributes={selectedAttributes}
                cancel={cancelEditAppPrioritization}
                saveAttributeConfig={saveAttrConfig}
                getCriticality={getCriticality}
                calculateRanking={calculateRanking}
                loading={loading}
            />
        );
    } else if (appPrioritizationState === STAGE_VALUES.START_OVER) {
        const newAttrSelectOptions = attrSelectOptions.filter(option => option.selected).map(option => {
            const prioritizableSchema = applicationColumnDefinitions?.find(columnDefinition => columnDefinition.id === option.value);
            return {
                ...option,
                selected: false,
                partition: prioritizableSchema ? 
                    buildAttributePartitionFromValues(prioritizableSchema, getUniqueAttributeValues(prioritizableSchema, applicationItems)) : 
                    option.partition
            };
        });
        const oldAttrSelectOptions = attrSelectOptions.filter(option => !option.selected);
        return (
            <ApplicationPrioritizationWizard
                attrSelectionOptions={[...oldAttrSelectOptions, ...newAttrSelectOptions]}
                selectedAttributes={[]}
                cancel={cancelEditAppPrioritization}
                saveAttributeConfig={saveAttrConfig}
                getCriticality={getCriticality}
                calculateRanking={calculateRanking}
                loading={loading}
            />
        );
    } else {
        return null;
    }
    
}

export default withFlashContext(ApplicationPrioritization);
